/**
 * Per-platform linking card used inside the wizard's Step 2.
 *
 * Two entry modes:
 *   1. Paste URL — backend extracts platformIds from a native ad-manager URL.
 *   2. Manual ID entry — fallback, also used when the parser doesn't match.
 *
 * After IDs are entered (either way), clicking Validate runs a dry-run
 * against the platform's API. The wizard does not require validation to
 * succeed (the team may register IDs before creds are provisioned), but the
 * resolved campaign name is the strongest signal that the link is correct.
 */

import React, { useState } from 'react';
import type {
  CgBoostPlatform,
  CgBoostPlatformIds,
  CgBoostValidateLinkResult,
} from '../../types/cgBoost';
import {
  PLATFORM_LABELS,
  parsePlatformLinkUrl,
  validatePlatformLink,
} from '../../services/cgBoostApi';
import { Pill, Button } from '../ui';

type YouTubeIds = NonNullable<CgBoostPlatformIds['youtube']>;
type TikTokIds = NonNullable<CgBoostPlatformIds['tiktok']>;
type MetaIds = NonNullable<CgBoostPlatformIds['meta']>;
export type AnyPlatformIds = YouTubeIds | TikTokIds | MetaIds;

interface Props {
  platform: CgBoostPlatform;
  /** Current platformIds for this platform — undefined = not yet linked. */
  value: AnyPlatformIds | undefined;
  onChange: (ids: AnyPlatformIds | undefined) => void;
}

const PLATFORM_URL_HINTS: Record<CgBoostPlatform, string> = {
  tiktok: 'Paste from TikTok Ads Manager (campaign list URL)',
  meta: 'Paste from Meta Ads Manager (campaign list URL)',
  youtube: 'Paste from Google Ads (campaign overview URL)',
};

const PlatformLinkCard: React.FC<Props> = ({ platform, value, onChange }) => {
  const [urlInput, setUrlInput] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<CgBoostValidateLinkResult | null>(null);

  const linked = !!value;

  const applyParsedUrl = async (): Promise<void> => {
    setParseError(null);
    setValidation(null);
    if (!urlInput.trim()) return;
    const parsed = await parsePlatformLinkUrl(urlInput.trim());
    if (!parsed) {
      setParseError("Couldn't parse a campaign URL — check the format or use Manual entry below.");
      return;
    }
    if (parsed.platform !== platform) {
      setParseError(
        `URL is for ${PLATFORM_LABELS[parsed.platform]}, but this is the ${PLATFORM_LABELS[platform]} card.`,
      );
      return;
    }
    onChange(parsed.ids as AnyPlatformIds);
    setUrlInput('');
  };

  const runValidate = async (): Promise<void> => {
    if (!value) return;
    setValidating(true);
    setValidation(null);
    try {
      const result = await validatePlatformLink(platform, value as Record<string, string>);
      setValidation(result);
    } catch (err: any) {
      setValidation({ validated: false, reason: err.message ?? 'Validate request failed' });
    } finally {
      setValidating(false);
    }
  };

  const clearLink = (): void => {
    onChange(undefined);
    setValidation(null);
  };

  return (
    <div style={card}>
      <div style={cardHeader}>
        <div style={cardTitle}>{PLATFORM_LABELS[platform]}</div>
        {linked ? (
          <Pill tone="success" size="sm">Linked</Pill>
        ) : (
          <Pill tone="neutral" size="sm">Not linked</Pill>
        )}
      </div>

      {!linked && (
        <div style={section}>
          <label style={fieldLabel}>{PLATFORM_URL_HINTS[platform]}</label>
          <div style={urlRow}>
            <input
              style={input}
              type="url"
              placeholder="https://…"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
            />
            <Button variant="subtle" size="sm" onClick={applyParsedUrl} disabled={!urlInput.trim()}>
              Parse
            </Button>
          </div>
          {parseError && <div style={errorLine}>{parseError}</div>}
        </div>
      )}

      <div style={section}>
        <label style={fieldLabel}>{linked ? 'Linked IDs' : 'Manual entry'}</label>
        <ManualIdsEditor platform={platform} value={value} onChange={onChange} disabled={false} />
      </div>

      {linked && (
        <div style={validateRow}>
          <Button variant="subtle" size="sm" onClick={runValidate} disabled={validating}>
            {validating ? 'Validating…' : 'Validate link'}
          </Button>
          <Button variant="ghost" size="sm" onClick={clearLink}>
            Clear
          </Button>
        </div>
      )}

      {validation && <ValidationResult result={validation} />}
    </div>
  );
};

// ----- Manual ID editor (per-platform fields) -----

const ManualIdsEditor: React.FC<{
  platform: CgBoostPlatform;
  value: AnyPlatformIds | undefined;
  onChange: (ids: AnyPlatformIds | undefined) => void;
  disabled: boolean;
}> = ({ platform, value, onChange }) => {
  const update = (next: AnyPlatformIds): void => {
    // If both required fields are empty, treat as cleared.
    const hasAny = Object.values(next).some((v) => typeof v === 'string' && v.trim().length > 0);
    onChange(hasAny ? next : undefined);
  };

  if (platform === 'tiktok') {
    const v = (value as TikTokIds | undefined) ?? { campaignId: '', advertiserId: '' };
    return (
      <div style={twoCol}>
        <Field label="Campaign ID">
          <input style={input} value={v.campaignId} onChange={(e) => update({ ...v, campaignId: e.target.value })} />
        </Field>
        <Field label="Advertiser ID">
          <input style={input} value={v.advertiserId} onChange={(e) => update({ ...v, advertiserId: e.target.value })} />
        </Field>
      </div>
    );
  }
  if (platform === 'meta') {
    const v = (value as MetaIds | undefined) ?? { campaignId: '', adAccountId: '' };
    return (
      <div style={twoCol}>
        <Field label="Campaign ID">
          <input style={input} value={v.campaignId} onChange={(e) => update({ ...v, campaignId: e.target.value })} />
        </Field>
        <Field label="Ad account ID">
          <input style={input} value={v.adAccountId} onChange={(e) => update({ ...v, adAccountId: e.target.value })} />
        </Field>
      </div>
    );
  }
  // youtube → Google Ads
  const v = (value as YouTubeIds | undefined) ?? { customerId: '', campaignId: '' };
  return (
    <div style={twoCol}>
      <Field label="Customer ID (10-digit)">
        <input
          style={input}
          value={v.customerId}
          placeholder="1234567890"
          onChange={(e) => update({ ...v, customerId: e.target.value.replace(/-/g, '') })}
        />
      </Field>
      <Field label="Campaign ID">
        <input style={input} value={v.campaignId} onChange={(e) => update({ ...v, campaignId: e.target.value })} />
      </Field>
      <Field label="Login customer ID (optional MCC override)">
        <input
          style={input}
          value={v.loginCustomerId ?? ''}
          placeholder="(uses env default)"
          onChange={(e) => update({ ...v, loginCustomerId: e.target.value.replace(/-/g, '') || undefined })}
        />
      </Field>
    </div>
  );
};

// ----- Validation result block -----

const ValidationResult: React.FC<{ result: CgBoostValidateLinkResult }> = ({ result }) => {
  if (!result.validated) {
    return (
      <div style={infoBox}>
        <Pill tone="info" size="sm">Couldn't validate</Pill>
        <span style={validateNote}>{result.reason}. You can still save the link.</span>
      </div>
    );
  }
  if (!result.ok) {
    return (
      <div style={errorBox}>
        <Pill tone="danger" size="sm">Failed</Pill>
        <ul style={errList}>
          {result.errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      </div>
    );
  }
  const r = result.resolved ?? {};
  return (
    <div style={successBox}>
      <Pill tone="success" size="sm">Validated</Pill>
      <dl style={dl}>
        {r.campaignName && <Row label="Campaign" value={r.campaignName} />}
        {r.advertiserName && <Row label="Advertiser" value={r.advertiserName} />}
        {r.status && <Row label="Status" value={r.status} />}
        {r.startDate && <Row label="Start" value={r.startDate} />}
        {r.endDate && <Row label="End" value={r.endDate} />}
        {r.currency && <Row label="Currency" value={r.currency} />}
        {r.timezone && <Row label="Timezone" value={r.timezone} />}
      </dl>
      {result.warnings.length > 0 && (
        <ul style={warnList}>
          {result.warnings.map((w, i) => (
            <li key={i}>⚠️ {w}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <span style={miniLabel}>{label}</span>
    {children}
  </label>
);

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <>
    <dt style={dt}>{label}</dt>
    <dd style={dd}>{value}</dd>
  </>
);

// ----- styles -----

const card: React.CSSProperties = {
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: 14,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  background: '#fff',
};
const cardHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10 };
const cardTitle: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: 'var(--fg-1)', flex: 1 };

const section: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };

const fieldLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--fg-3)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
};
const miniLabel: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, color: 'var(--fg-3)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
};

const urlRow: React.CSSProperties = { display: 'flex', gap: 8 };
const twoCol: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
};

const validateRow: React.CSSProperties = { display: 'flex', gap: 8 };

const input: React.CSSProperties = {
  flex: 1,
  padding: '8px 11px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border-2)',
  fontSize: 13,
  color: 'var(--fg-1)',
  background: '#fff',
  fontFamily: 'var(--font-sans)',
  outline: 'none',
};

const errorLine: React.CSSProperties = {
  fontSize: 12, color: 'var(--danger)',
};
const validateNote: React.CSSProperties = { fontSize: 12.5, color: 'var(--fg-3)' };

const infoBox: React.CSSProperties = {
  background: 'var(--info-soft)',
  padding: 10,
  borderRadius: 'var(--radius-sm)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};
const errorBox: React.CSSProperties = {
  background: 'var(--danger-soft)',
  padding: 10,
  borderRadius: 'var(--radius-sm)',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
const successBox: React.CSSProperties = {
  background: 'var(--success-soft)',
  padding: 10,
  borderRadius: 'var(--radius-sm)',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const errList: React.CSSProperties = {
  margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--danger)',
};
const warnList: React.CSSProperties = {
  margin: 0, paddingLeft: 0, listStyle: 'none', fontSize: 12, color: 'var(--warning)',
};

const dl: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '110px 1fr',
  rowGap: 4, columnGap: 10,
  margin: 0, fontSize: 12.5,
};
const dt: React.CSSProperties = { color: 'var(--fg-3)', fontWeight: 600 };
const dd: React.CSSProperties = { margin: 0, color: 'var(--fg-1)' };

export default PlatformLinkCard;
